import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Form, Input, Select, Popconfirm, message, Tag, Row, Col } from 'antd';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

import axiosRestaurantInstance from './AxiosRestaurantInstance';

import './css/UserManagement.css';

const UserManagementAdmin = () => {
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(''); // For fetching users
  const [formRestaurantId, setFormRestaurantId] = useState(''); // For the Add/Edit form
  const [formRestaurantName, setFormRestaurantName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const user = JSON.parse(localStorage.getItem('user'));
  const userId = user ? user.userId : null;

  // Fetch restaurants for the logged-in user
  useEffect(() => {
    const fetchRestaurants = async () => {
      if (!userId) return;

      try {
        const response = await axiosRestaurantInstance.get(`/user_restaurant/${userId}`);
        setRestaurants(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching restaurants:', error.response?.data || error.message);
      }
    };

    fetchRestaurants();
  }, [userId]);

  // Fetch users for the selected restaurant (used for listing)
  useEffect(() => {
    if (selectedRestaurantId) {
      const fetchUsers = async () => {
        try {
          const response = await axiosRestaurantInstance.get(`/users/${selectedRestaurantId}`);
  
          console.log("API Response:", response.data); // Debugging
  
          const usersArray = response.data.users || []; // Ensure it's always an array
  
          const formattedUsers = usersArray.map((user) => ({
            id: user.userId,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            mobile: user.mobile,
            roles: user.roles,
          }));
  
          setUsers(formattedUsers);
        } catch (error) {
          console.error('Error fetching users:', error);
          message.error('Failed to fetch users');
        }
      };
  
      fetchUsers();
    }
  }, [selectedRestaurantId]);
  

  const showModal = (user = null) => {
    setEditingUser(user);
    setIsModalOpen(true);
    form.resetFields();

    if (user) {
      form.setFieldsValue({
        first_name: user.firstName,
        last_name: user.lastName,
        email: user.email,
        mobile: user.mobile,
        restaurant_id: formRestaurantId,
        restaurant_name: formRestaurantName,
        role_names: user.roles || [],
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    const payload = {
      ...values,
      restaurant_id: formRestaurantId, // Use form-specific restaurant ID
      restaurant_name: formRestaurantName, // Use form-specific restaurant name
      fcm_token: null,
    };

    if (!payload.role_names || payload.role_names.length === 0) {
      message.error('At least one role (Manager or DeliveryAgent) is required');
      setLoading(false);
      return;
    }

    if (!payload.password) {
      message.error('Password is required');
      setLoading(false);
      return;
    }

    try {
      if (editingUser) {
        await axiosRestaurantInstance.put(`/update_user/${editingUser.id}`, payload);
        message.success('User updated successfully');
      } else {
        await axiosRestaurantInstance.post('/restaurant-user', payload);
        message.success('User added successfully');
      }

      form.resetFields();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error submitting user:', error.response?.data || error.message);
      message.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axiosRestaurantInstance.delete(`/delete_user/${id}`);
      if (response.status === 200) {
        setUsers(users.filter((user) => user.id !== id));
        message.success('User deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const columns = [
    { title: 'User ID', dataIndex: 'id', key: 'id' },
    { title: 'First Name', dataIndex: 'firstName', key: 'firstName' },
    { title: 'Last Name', dataIndex: 'lastName', key: 'lastName' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Mobile', dataIndex: 'mobile', key: 'mobile' },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles) => roles.map((role) => <Tag key={role}>{role}</Tag>),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span>
          <Button type="link" onClick={() => showModal(record)}>
            <PencilSquareIcon className="custom-icon edit-icon" />
          </Button>
          <Popconfirm
            title="Are you sure to delete this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              <TrashIcon className="custom-icon delete-icon" />
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div className="user-management">
      <div className="top-bar" style={{ marginBottom: 16, display: 'flex',alignItems: 'center'}}>
        <Input
          placeholder="Search users"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: 200, marginRight: 10 }}
        />
        <Select
          placeholder="Select Restaurant"
          value={selectedRestaurantId || undefined}
          onChange={(value) => setSelectedRestaurantId(value)}
          style={{ width: 200, marginRight: 10}}
                  >
          {restaurants.map((restaurant) => (
            <Select.Option key={restaurant.RestaurantId} value={restaurant.RestaurantId}>
              {restaurant.RestaurantName}
            </Select.Option>
          ))}
        </Select>

        <Button type="primary" onClick={() => showModal()}>
          Add User
        </Button>
      </div>

      <Table dataSource={users.filter(user => `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()))} columns={columns} rowKey="id" />

      <Modal
        title={editingUser ? `Edit User - ID: ${editingUser.id}` : 'Add User'}
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            first_name: '',
            last_name: '',
            mobile: '',
            email: '',
            restaurant_id: '',
            role_names: [],
            password: '',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[{ required: true, message: 'First Name is required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="last_name"
                rules={[{ required: true, message: 'Last Name is required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Mobile"
                name="mobile"
                rules={[{ required: true, message: 'Mobile number is required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Invalid email format' },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: 'Password is required' },
                  { min: 6, message: 'Password must be at least 6 characters long' },
                ]}
              >
                <Input.Password />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Restaurant"
                name="restaurant_id"
                rules={[{ required: true, message: 'Please select a restaurant' }]}
              >
                <Select
                  placeholder="Select Restaurant"
                  value={formRestaurantId}
                  onChange={(value, option) => {
                    setFormRestaurantId(value);
                    setFormRestaurantName(option.children);
                    form.setFieldsValue({
                      restaurant_id: value,
                      restaurant_name: option.children,
                    });
                  }}
                >
                  {restaurants.map((restaurant) => (
                    <Select.Option key={restaurant.RestaurantId} value={restaurant.RestaurantId}>
                      {restaurant.RestaurantName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Role"
                name="role_names"
                rules={[{ required: true, message: 'Please select at least one role' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select roles"
                  options={[
                    { label: 'Manager', value: 'Manager' },
                    { label: 'DeliveryAgent', value: 'DeliveryAgent' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {editingUser ? 'Update User' : 'Add User'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagementAdmin;
